import { CurrencyCode } from '../services/api.service';

type CurrencyFormat = {
  symbol: string;
  position: 'prefix' | 'suffix';
  decimalSeparator: 'dot' | 'comma';
};

type KnownCurrencyCode = 'USD' | 'EUR' | 'PLN' | 'GBP' | 'INR';

const CURRENCY_FORMATS: Record<KnownCurrencyCode, CurrencyFormat> = {
  USD: { symbol: '$', position: 'prefix', decimalSeparator: 'dot' },
  EUR: { symbol: '€', position: 'prefix', decimalSeparator: 'comma' },
  GBP: { symbol: '£', position: 'prefix', decimalSeparator: 'dot' },
  PLN: { symbol: 'zł', position: 'suffix', decimalSeparator: 'comma' },
  INR: { symbol: '₹', position: 'prefix', decimalSeparator: 'dot' },
};

const DEFAULT_FORMAT: CurrencyFormat = {
  symbol: '',
  position: 'prefix',
  decimalSeparator: 'dot',
};

export function getCurrencyFormat(currency: CurrencyCode): CurrencyFormat {
  if (!currency) {
    return DEFAULT_FORMAT;
  }

  const normalizedCurrency = currency.toUpperCase() as KnownCurrencyCode;

  return CURRENCY_FORMATS[normalizedCurrency] ?? DEFAULT_FORMAT;
}

export function getCurrencySymbol(currency: CurrencyCode): string {
  return getCurrencyFormat(currency).symbol;
}

export function formatCurrency(
  currency: CurrencyCode,
  amount: number | undefined,
  options: { minimumFractionDigits?: number } = {}
): string {
  if (!amount) {
    return '';
  }

  const format = getCurrencyFormat(currency);
  const digits = options.minimumFractionDigits ?? 2;
  // Drop trailing zero decimals so whole amounts render without ".00"
  // (e.g. INR ₹2,499 instead of ₹2,499.00). Fractional amounts keep their decimals.
  const numeric = amount.toFixed(digits).replace(/\.0+$/, '');
  const formattedNumber = format.decimalSeparator === 'comma' ? numeric.replace('.', ',') : numeric;

  if (!format.symbol) {
    return formattedNumber;
  }

  return format.position === 'suffix'
    ? `${formattedNumber} ${format.symbol}`
    : `${format.symbol}${formattedNumber}`;
}
